import type { AssetLOTO } from "@/pages/asset-details/models/AssetLOTO";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import AddBoxIcon from "@material-ui/icons/AddBox";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CancelIcon from "@material-ui/icons/Cancel";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	SelectionChangedEvent,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoadingOverlay from "../../../../../components/loaders/LazyLoading/LazyLoading";
import { useAssetDetailsContext } from "../../../context/assetDetailsContext";
import { addLinkAssetLOTO } from "../../../repositories/AssetDetailsRepositorie";
import { useInitialValues } from "../../hooks/useInitialValues";
import { useSearchParamsToReducer } from "../../hooks/useSearchParamsToReducer";
import { CommentsDialog } from "./components/comment-dialog/comment-dialog";
import { FirstCommentDialog } from "./components/comment-dialog/firstComment-dialog";
import { LOTOAssetNameColumn } from "./components/editing-row/LOTO-AssetName.column";
import { LOTONameColumn } from "./components/editing-row/LOTO-LOTOName.column";
import { LOTONumberColumn } from "./components/editing-row/LOTO-LOTONumber.column";
import { LOTOLinkButton } from "./components/link-button/LOTO-link-button.columns";
import { useAssetLOTOsGridActionsAndButtons } from "./hooks/useAssetLOTOsGridActionsAndButtons";
import { useSearchAssetLOTO } from "./hooks/useSearchAssetLOTO";
import { ErrorModal } from "@dexteel/mesf-core";
import FormatListBulletedSharpIcon from "@material-ui/icons/FormatListBulletedSharp";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

const useStyles = makeStyles((theme) =>
	createStyles({
		agGridContainer: {
			position: "relative",
		},
		searchToolbar: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			padding: "8px 0px",
		},
		searchField: {
			flex: 1,
			marginRight: 0,
			"& .MuiOutlinedInput-root": {
				height: "40px",
			},
		},
		gridWrapper: {
			height: "calc(100% - 60px)",
		},
		btnAdd: {
			position: "absolute",
			padding: 0,
			bottom: 0,
			right: 0,
		},
		btnDelete: {
			position: "absolute",
			padding: 0,
			bottom: 0,
			right: 43,
		},
		btnCached: {
			position: "absolute",
			padding: 0,
			bottom: 0,
			left: 0,
		},
	}),
);

type Props = {
	searchStatistics: Function;
};

export const AssetLOTOGrid = ({ searchStatistics }: Props) => {
	const navigate = useNavigate();
	const classes = useStyles();
	const [openViewer, setOpenViewer] = useState<boolean>(false);
	const [openCommentsDialog, setOpenCommentsDialog] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");
	const [quickFilterText, setQuickFilterText] = useState("");
	const [includeDescendants, setIncludeDescendants] = useState(true);
	const searchAssetLOTO = useSearchAssetLOTO({ setIsLoading, setError });
	const [isLinkLOTOLoading, setIsLinkLOTOLoading] = useState<boolean>(true);
	const [linkAssetLOTOError, setLinkAssetLOTOError] = useState<string>("");
	const [isRemoveLOTOLoading, setIsRemoveLOTOLoading] = useState<boolean>(true);
	const [removeAssetLOTOError, setRemoveAssetLOTOError] = useState<string>("");

	const {
		state: {
			assetSearchId,
			listAssetLOTO,
			rowSelected,
			openAddCommentDialog,
			LOTONumberToLink,
			LOTONameToLink,
		},
		actions: { setRowSelected, setOpenAddCommentDialog },
	} = useAssetDetailsContext();

	const [gridApi, setGridRef] = useState<GridApi | null>(null);
	const containerStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [listLOTOSelected, setListLOTOSelected] = useState<any[]>([]);
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();
	const [searchParams] = useSearchParams();
	const resource = searchParams.get("resource") ?? "";

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const {
		refresh,
		saveRow,
		addRow,
		removeSelected,
		addButton,
		deleteButton,
		saveButton,
		discardButton,
		deleteOpen,
		setSaveButton,
		setDiscardButton,
		setAddButton,
		setDeleteButton,
		setDeleteOpen,
	} = useAssetLOTOsGridActionsAndButtons({
		gridRef: gridApi,
		searchStatistics,
		searchAssetLOTO,
		setIsLinkLOTOLoading,
		setLinkAssetLOTOError,
		setIsRemoveLOTOLoading,
		setRemoveAssetLOTOError,
		includeDescendants,
	});

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "",
			flex: 1,
			minWidth: 50,
			maxWidth: 50,
			filter: false,
			checkboxSelection: true,
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			getQuickFilterText: () => "",
		});
		newColumns.push({
			cellRenderer: LOTONameColumn,
			field: "LOTONumber",
			headerName: "LOTO N°",
			flex: 2,
			minWidth: 150,
			maxWidth: 150,
			type: "columnProperties",
		});
		newColumns.push({
			cellRenderer: LOTONumberColumn,
			field: "LOTOName",
			headerName: "LOTO Name",
			flex: 3,
			minWidth: 290,
			type: "columnProperties",
		});
		newColumns.push({
			cellRenderer: LOTOAssetNameColumn,
			field: "AssetName",
			headerName: "AssetName",
			flex: 2,
			minWidth: 320,
			type: "columnProperties",
		});
		newColumns.push({
			cellRenderer: LOTOLinkButton,
			field: "LOTO view",
			headerName: "LOTO",
			flex: 1,
			minWidth: 80,
			maxWidth: 80,
			type: "columnProperties",
		});
		newColumns.push({
			headerName: "",
			flex: 1,
			minWidth: 65,
			maxWidth: 65,
			cellClass: "ag-columns",
			wrapText: false,
			autoHeight: false,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid
							item
							xs={12}
							md={12}
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid item xs={12} md={12}>
									<Button
										fullWidth
										style={{}}
										onClick={(e) => onActionsClick(e, params)}
									>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				);
			},
		});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			// Clear previous selections when right-clicking on a single row
			if (gridApi && params.node) {
				const isCurrentlySelected = params.node.isSelected();
				if (!isCurrentlySelected) {
					gridApi.deselectAll();
					params.node.setSelected(true);
				}
			}

			const rowSelectedInGrid = params.node?.data;
			setRowSelected(rowSelectedInGrid);
			const selectedLOTOs = gridApi?.getSelectedNodes();
			const LOTOsLinksToPaste = JSON.parse(
				localStorage.getItem("LOTOCopied") as string,
			);

			const LinkToLOTO = () => {
				const link = document.createElement("a");
				link.href = `https://web.conformit.com/Print.aspx?Prockey=${params.node?.data.LOTONumber}`;
				link.setAttribute("target", `_blank}`);
				document.body.appendChild(link);
				link.click();
			};

			const LOTOsCopiedInLocalStorage = () => {
				if (selectedLOTOs?.length) {
					const selectedLOTOsId = selectedLOTOs.map((rowNode) => rowNode.data);
					localStorage.setItem("LOTOCopied", JSON.stringify(selectedLOTOsId));
				}
			};

			const PasteLOTOsCopiedInLocalStorage = () => {
				LOTOsLinksToPaste.forEach(async (LOTO: AssetLOTO) => {
					setIsLinkLOTOLoading(true);
					const addLOTOLink = await addLinkAssetLOTO(
						assetSearchId as string,
						LOTO.LOTONumber as string,
						LOTO.LOTOName as string,
						"",
					);
					if (addLOTOLink.ok) {
						setIsLinkLOTOLoading(false);
					} else {
						setIsLinkLOTOLoading(false);
						setLinkAssetLOTOError(addLOTOLink.message);
					}
				});
				refresh();
			};

			const result: (DefaultMenuItem | MenuItemDef)[] = [];

			if (selectedLOTOs?.length == 1) {
				result.push({
					name: "Go to Asset",
					action: () => {
						setSaveButton(false);
						setDiscardButton(false);
						setAddButton(true);
						setDeleteButton(false);
						setDeleteOpen(false);
						setTimeout(() => {
							navigate(`/asset-details/${params.node?.data.AssetId}`, {
								replace: true,
							});
							setIsLoading(false);
						}, 100);
					},
					icon: '<i class="fas fa-external-link-alt"></i>',
				});
			}

			if (rowSelectedInGrid) {
				result.push({
					name: "See comments",
					action: () => {
						setOpenCommentsDialog(!openCommentsDialog);
					},
					icon: '<i class="far fa-comment-alt"></i>',
				});
			}

			if (rowSelectedInGrid) {
				result.push({
					name: "See LOTO",
					action: () => {
						LinkToLOTO();
					},
					icon: '<i class="fas fa-tag"></i>',
				});
			}

			if (!saveButton) {
				result.push({
					name: "Add LOTO Link",
					action: addRow,
					icon: '<i class="fas fa-plus"></i>',
				});
			}

			if (saveButton) {
				result.push({
					name: "Save Link",
					action: () => {
						if (LOTONumberToLink === "" || LOTONameToLink === "") {
							setError("LOTO number and LOTO npo11ame are required");
							return;
						} else setOpenAddCommentDialog(true);
					},
					icon: '<i class="fas fa-save"></i>',
				});
			}

			if (selectedLOTOs?.length) {
				result.push({
					name: "Copy LOTO Link",
					action: LOTOsCopiedInLocalStorage,
					icon: '<i class="far fa-copy"></i>',
				});
			}

			if (LOTOsLinksToPaste?.length) {
				result.push({
					name: "Paste LOTO Link",
					action: PasteLOTOsCopiedInLocalStorage,
					icon: '<i class="fas fa-paste"></i>',
				});
			}

			if (!selectedLOTOs?.length && rowSelectedInGrid) {
				result.push({
					name: "Remove LOTO Link",
					action: () => {
						setListLOTOSelected([]);
						setRowSelected(params.node?.data);
						setDeleteOpen((prev: boolean) => !prev);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			if (deleteButton) {
				result.push({
					name: "Remove LOTO Link",
					action: () => {
						setDeleteOpen(true);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			result.push(
				"separator" as DefaultMenuItem,
				"excelExport" as DefaultMenuItem,
				{
					name: "Refresh",
					action: () => refresh(),
					icon: '<i class="fas fa-sync-alt"></i>',
				},
			);

			return result;
		},
		[
			listAssetLOTO,
			deleteButton,
			saveButton,
			addRow,
			assetSearchId,
			LOTONumberToLink,
			LOTONameToLink,
			listLOTOSelected,
			listAssetLOTO,
		],
	);

	const onSelectionChanged = useCallback((event: SelectionChangedEvent) => {
		const rowCount = event.api.getSelectedNodes().length;
		const listLOTOsSelected = event.api.getSelectedNodes();
		setListLOTOSelected(listLOTOsSelected);
		rowCount > 0 ? setDeleteButton(true) : setDeleteButton(false);
	}, []);

	const openModalViewerActive = () => {
		const selectedRow = gridApi?.getSelectedNodes();
		setRowSelected(selectedRow && selectedRow[0].data);
		setOpenViewer(!openViewer);
	};
	// ================

	useInitialValues();
	useSearchParamsToReducer();

	useEffect(() => {
		setSaveButton(false);
		setDiscardButton(false);
		setAddButton(true);
		setDeleteButton(false);
		setDeleteOpen(false);
	}, [assetSearchId]);

	useEffect(() => {
		renderColumns();
	}, []);

	const handleQuickFilterChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setQuickFilterText(value);
			if (gridApi) {
				gridApi.setGridOption("quickFilterText", value);
			}
		},
		[gridApi],
	);

	useEffect(() => {
		// Only search if we have a valid assetSearchId (not 0, undefined, or null)
		// This prevents the initial call with assetSearchId=0 when the component first mounts
		if (resource === "loto" && assetSearchId && assetSearchId !== 0) {
			searchAssetLOTO(includeDescendants);
		}
	}, [resource, assetSearchId, includeDescendants]);

	return (
		<>
			<div style={containerStyle}>
				<Grid container style={gridStyle}>
					<Grid
						item
						md={12}
						xs={12}
						style={{ paddingBottom: 45 }}
						className={classes.agGridContainer}
					>
						<div className={classes.searchToolbar}>
							<TextField
								className={classes.searchField}
								variant="outlined"
								placeholder="Search LOTO..."
								value={quickFilterText}
								onChange={handleQuickFilterChange}
								InputProps={{
									startAdornment: (
										<SearchIcon color="action" style={{ marginRight: 8 }} />
									),
								}}
								size="small"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={includeDescendants}
										onChange={(e) => setIncludeDescendants(e.target.checked)}
										color="primary"
									/>
								}
								label="Include descendant assets"
								style={{ marginLeft: 16, marginRight: 16 }}
							/>
						</div>

						<div className={classes.gridWrapper}>
							<AgGridReact
								onGridReady={(params) => setGridRef(params.api)}
								rowData={listAssetLOTO}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								headerHeight={32}
								rowSelection={"multiple"}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								onSelectionChanged={onSelectionChanged}
								loadingOverlayComponent={loadingOverlayComponent}
								onRowDoubleClicked={openModalViewerActive}
								loading={isLoading}
								quickFilterText={quickFilterText}
							/>
						</div>
						<CommentsDialog
							searchAssetLOTO={searchAssetLOTO}
							show={openCommentsDialog}
							rowSelected={rowSelected}
							onHide={() => setOpenCommentsDialog(false)}
						/>
						<FirstCommentDialog
							saveRow={saveRow}
							show={openAddCommentDialog}
							onHide={() => setOpenAddCommentDialog(false)}
						/>
						{addButton && (
							<>
								<IconButton
									onClick={addRow}
									aria-label="add"
									color="primary"
									className={classes.btnAdd}
								>
									<AddBoxIcon style={{ width: 35, height: 35 }} />
								</IconButton>
							</>
						)}
						{deleteButton && (
							<>
								<IconButton
									onClick={() => setDeleteOpen(true)}
									aria-label="delete"
									color="secondary"
									className={classes.btnDelete}
									style={{ right: 85 }}
								>
									<DeleteIcon style={{ width: 35, height: 35 }} />
								</IconButton>
							</>
						)}
						{saveButton && discardButton && (
							<>
								<IconButton
									onClick={() => setOpenAddCommentDialog(true)}
									aria-label="save"
									className={classes.btnAdd}
									color="primary"
								>
									<SaveIcon style={{ width: 36, height: 36 }} />
								</IconButton>
								<IconButton
									onClick={refresh}
									aria-label="cancel"
									className={classes.btnDelete}
									color="default"
								>
									<CancelIcon style={{ width: 35, height: 35 }} />
								</IconButton>
							</>
						)}
						<IconButton
							onClick={refresh}
							aria-label="delete"
							className={classes.btnCached}
						>
							<AutorenewIcon
								style={{ width: 30, height: 30, color: "green" }}
							/>
						</IconButton>
					</Grid>
					<Dialog open={deleteOpen} aria-labelledby="responsive-dialog-title">
						<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
						<DialogContent>
							<DialogContentText style={{ padding: 0, margin: 0 }}>
								{listLOTOSelected?.length === 1 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{listLOTOSelected[0].data.LOTOName}</b> FROM
											ASSET <b>{listLOTOSelected[0].data.AssetName}?</b>
										</p>
									</>
								) : listLOTOSelected.length > 1 ? (
									<>
										<p style={{ margin: 0 }}> UNLINK:</p>
										{listLOTOSelected?.map((LOTO) => {
											return (
												<>
													<p style={{ margin: "5px 0" }}>
														<b>⁃ {LOTO.data.LOTOName}, </b>
													</p>
													<p style={{ margin: 0 }}>
														FROM ASSET <b>{LOTO.data.AssetName}.</b>
													</p>
												</>
											);
										})}
									</>
								) : rowSelected?.LOTOName && listLOTOSelected?.length === 0 ? (
									<>
										<p style={{ margin: "0" }}>
											UNLINK <b>{rowSelected?.LOTOName}</b> FROM ASSET{" "}
											<b>{rowSelected?.AssetName}?</b>
										</p>
									</>
								) : (
									<>
										<h1>Something went wrong</h1>
									</>
								)}
							</DialogContentText>
						</DialogContent>
						<DialogActions style={{ padding: "16px 24px" }}>
							<Button
								onClick={() => setDeleteOpen(false)}
								autoFocus
								color="default"
							>
								CANCEL
							</Button>
							<Button autoFocus color="secondary" onClick={removeSelected}>
								UNLINK
							</Button>
						</DialogActions>
					</Dialog>
				</Grid>
				<ErrorModal error={error} onHide={() => setError("")} />
				<ErrorModal
					error={linkAssetLOTOError}
					onHide={() => setLinkAssetLOTOError("")}
				/>
				<ErrorModal
					error={removeAssetLOTOError}
					onHide={() => setRemoveAssetLOTOError("")}
				/>
			</div>
		</>
	);
};
